/**
 * Webtech HR Engine™ - Employee My Skills Page
 * Vue personnelle des compétences pour l'employé
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  Loader2,
  ChevronRight,
  Star,
  GraduationCap,
  Info,
  Trophy
} from 'lucide-react';

const LEVEL_CONFIG = {
  1: { label: 'Débutant', color: 'bg-red-500', description: 'Connaissances de base' },
  2: { label: 'Intermédiaire', color: 'bg-orange-500', description: 'Capable avec assistance' },
  3: { label: 'Confirmé', color: 'bg-yellow-500', description: 'Autonome' },
  4: { label: 'Avancé', color: 'bg-blue-500', description: 'Maîtrise approfondie' },
  5: { label: 'Expert', color: 'bg-green-500', description: 'Référent, peut former' }
};

const IMPORTANCE_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

export default function EmployeeMySkillsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [notLinked, setNotLinked] = useState(false);
  const [skillData, setSkillData] = useState(null);
  const [radarData, setRadarData] = useState(null);
  const [upskillingPlans, setUpskillingPlans] = useState([]);

  useEffect(() => {
    if (user?.id && user?.companyId) {
      fetchMyEmployee();
    }
  }, [user?.id, user?.companyId]);

  const fetchMyEmployee = async () => {
    try {
      // First get the employee linked to this user
      const empRes = await api.get(`/employee-accounts/my-employee?user_id=${user.id}&company_id=${user.companyId}`);
      
      if (!empRes.data.found) {
        setNotLinked(true);
        setLoading(false);
        return;
      }
      
      setEmployee(empRes.data.employee);
      await fetchMySkills(empRes.data.employee.id);
    } catch (error) {
      console.error('Error fetching employee:', error);
      setNotLinked(true);
      setLoading(false);
    }
  };

  const fetchMySkills = async (employeeId) => {
    try {
      const [profileRes, radarRes, plansRes] = await Promise.all([
        api.get(`/skills-intelligence/employee/${employeeId}?company_id=${user.companyId}`),
        api.get(`/skills-intelligence/employee/${employeeId}/radar?company_id=${user.companyId}`),
        api.get(`/skills-intelligence/upskilling-plans?company_id=${user.companyId}&employee_id=${employeeId}`)
      ]);
      
      setSkillData(profileRes.data);
      setRadarData(radarRes.data);
      setUpskillingPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Error fetching skills:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <DashboardLayout title="Mes compétences">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  // User not linked to an employee
  if (notLinked) {
    return (
      <DashboardLayout title="Mes compétences">
        <Card className="max-w-xl mx-auto mt-12">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Compte non lié
            </h2>
            <p className="text-slate-500 mb-6">
              Votre compte utilisateur n&apos;est pas encore lié à votre fiche employé.
              Contactez votre responsable RH pour activer votre accès.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 max-w-sm mx-auto">
              <p className="text-sm text-slate-500">Connecté en tant que :</p>
              <p className="font-medium text-slate-900">{user?.email}</p>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const { skillProfile, gaps } = skillData || {};
  const { summary = {} } = radarData || {};

  // No skill profile yet
  if (!skillProfile) {
    return (
      <DashboardLayout title="Mes compétences">
        <Card className="max-w-xl mx-auto mt-12">
          <CardContent className="pt-8 text-center">
            <Info className="w-16 h-16 mx-auto text-blue-400 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Profil de compétences non configuré
            </h2>
            <p className="text-slate-500 mb-6">
              Votre profil de compétences sera créé une fois qu&apos;un profil de poste vous sera assigné par votre responsable RH.
            </p>
            <p className="text-sm text-slate-400">
              Contactez votre Admin RH pour plus d&apos;informations.
            </p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Mes compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Mes compétences</h1>
            <p className="text-slate-500">Suivez votre progression et développez vos compétences</p>
          </div>
          {skillProfile.jobProfileName && (
            <Badge variant="outline" className="text-base px-4 py-2">
              <Target className="w-4 h-4 mr-2" />
              {skillProfile.jobProfileName}
            </Badge>
          )}
        </div>

        {/* Score Overview */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="md:col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center gap-8">
                {/* Score Gauge */}
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#e2e8f0"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={skillProfile.overallScore >= 80 ? '#22c55e' : skillProfile.overallScore >= 60 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${(skillProfile.overallScore / 100) * 352} 352`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className={`text-3xl font-bold ${getScoreColor(skillProfile.overallScore)}`}>
                      {skillProfile.overallScore}%
                    </span>
                    <span className="text-xs text-slate-500">Score global</span>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <Target className="w-6 h-6 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{summary.totalSkills || 0}</p>
                    <p className="text-sm text-slate-500">Compétences</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{summary.evaluatedSkills || 0}</p>
                    <p className="text-sm text-slate-500">Évaluées</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <AlertTriangle className="w-6 h-6 mx-auto text-red-500 mb-2" />
                    <p className="text-2xl font-bold text-red-600">{summary.criticalGaps || 0}</p>
                    <p className="text-sm text-slate-500">Écarts critiques</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Motivation Card */}
          <Card className="bg-gradient-to-br from-webtech-blue to-indigo-600 text-white">
            <CardContent className="pt-6">
              <Trophy className="w-8 h-8 mb-4 text-white/80" />
              <h3 className="text-lg font-semibold mb-2">
                {skillProfile.overallScore >= 80 ? 'Excellent travail !' :
                 skillProfile.overallScore >= 60 ? 'Continuez comme ça !' :
                 'Vous progressez !'}
              </h3>
              <p className="text-sm text-white/80">
                {skillProfile.overallScore >= 80 
                  ? 'Vos compétences sont au niveau attendu. Continuez à vous perfectionner !'
                  : 'Concentrez-vous sur les compétences marquées comme critiques pour progresser.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Skills List */}
        <Card>
          <CardHeader>
            <CardTitle>Détail de mes compétences</CardTitle>
            <CardDescription>Votre niveau actuel par rapport au niveau requis pour votre poste</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {skillProfile.skills?.map((skill, idx) => {
                const gapStatus = skill.gap === 0 ? 'achieved' : skill.importance === 'critical' && skill.gap >= 2 ? 'critical' : 'gap';
                
                return (
                  <div key={idx} className="p-4 rounded-lg border bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {gapStatus === 'achieved' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : gapStatus === 'critical' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        ) : (
                          <TrendingUp className="w-5 h-5 text-amber-500" />
                        )}
                        <div>
                          <h4 className="font-medium text-slate-900">
                            {skill.skillName || skill.skillId?.substring(0, 12)}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={IMPORTANCE_COLORS[skill.importance]}>
                              {skill.importance === 'critical' ? 'Critique' : skill.importance === 'medium' ? 'Moyen' : 'Faible'}
                            </Badge>
                            {skill.gap > 0 && (
                              <span className="text-xs text-red-500 font-medium">
                                -{skill.gap} niveau(x) à combler
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <div className="text-center">
                            <span className={`text-lg font-bold ${skill.currentLevel > 0 ? getScoreColor((skill.currentLevel / skill.requiredLevel) * 100) : 'text-slate-400'}`}>
                              {skill.currentLevel || '?'}
                            </span>
                            <span className="text-slate-400 mx-1">/</span>
                            <span className="text-lg font-bold text-slate-600">{skill.requiredLevel}</span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">
                          {LEVEL_CONFIG[skill.currentLevel]?.label || 'Non évalué'} → {LEVEL_CONFIG[skill.requiredLevel]?.label}
                        </p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="relative">
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${getScoreBgColor((skill.currentLevel / skill.requiredLevel) * 100)}`}
                          style={{ width: `${Math.min(100, (skill.currentLevel / skill.requiredLevel) * 100)}%` }}
                        />
                      </div>
                      {/* Required level marker */}
                      <div
                        className="absolute top-0 w-1 h-3 bg-slate-600"
                        style={{ left: '100%', transform: 'translateX(-50%)' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upskilling Plans */}
        {upskillingPlans.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-green-500" />
                Mes plans de développement
              </CardTitle>
              <CardDescription>Plans d&apos;upskilling proposés pour vous</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upskillingPlans.map((plan, idx) => (
                  <div key={idx} className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{plan.skillName}</h4>
                        <p className="text-sm text-slate-500">
                          Niveau {plan.currentLevel} → {plan.targetLevel}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={plan.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {plan.status === 'active' ? 'En cours' : 'Terminé'}
                        </Badge>
                        <Progress value={plan.progress || 0} className="w-24 h-2" />
                        <span className="text-sm font-medium">{plan.progress || 0}%</span>
                      </div>
                    </div>
                    {plan.actions?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <p className="text-xs text-slate-500 mb-2">{plan.actions.length} action(s) à réaliser</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card className="bg-slate-50">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-slate-900">Comment améliorer mes compétences ?</h4>
                <p className="text-sm text-slate-500">
                  Concentrez-vous sur les compétences marquées comme &ldquo;critiques&rdquo;. Votre manager peut vous proposer des plans de développement personnalisés.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
